import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin
} from 'antd'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useParams } from 'react-router-dom'
import { client } from '~/api/base'
import { getRegistration } from '~/api/genericApi'
import RegistrationLayout from '~/components/RegistrationLayout'
import { showErrors } from '~/utils/helpers'

const apiCheckout = async (registrationId: number) => {
  const res = await client.post(`/api/registrations/${registrationId}/pay`)
  return res.data
}

export default function RegistrationSummary() {
  const [checked, setChecked] = useState(false)
  const { id } = useParams()
  const queryClient = useQueryClient()
  const registrationQuery = useQuery({
    queryKey: ['registration', id],
    queryFn: async () => await getRegistration(id)
  })
  const [openModal, setOpenModal] = useState(false)
  const [form] = Form.useForm<{ coupon: string }>()

  const addCouponCodeMutation = useMutation({
    mutationFn: async (couponCode: string) => {
      await client.post(`/api/registrations/${id}/apply-coupon`, {
        coupon_code: couponCode
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registration'] })
      message.success({ content: 'Coupon applied successfully' })
      setOpenModal(false)
    },
    onError: showErrors
  })

  // const removeCouponCodeMutation = useMutation({
  //   mutationFn: async () =>
  //     await client.post('/api/registrations/id/remove-coupon'),
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: ['registration'] })
  //   }
  // })

  const checkoutMutation = useMutation({
    mutationFn: async (registrationId: number) =>
      await apiCheckout(registrationId),
    onSuccess: (data) => {
      if (data.code === 'no_payment_required') {
        window.location.href = data.success_url
        return
      }
      window.location.href = data.checkout_url
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ['registration'] })
      showErrors(error)
    }
  })

  const submitCoupon = async () => {
    const values = await form.validateFields()
    addCouponCodeMutation.mutate(values.coupon)
  }

  if (registrationQuery.isLoading) {
    return (
      <div className='centered-container'>
        <Spin size='large' />
      </div>
    )
  }

  const registration = registrationQuery.data
  if (registration == null) {
    return null
  }

  const course = registration.course
  const currency = course?.currency ?? 'USD'

  if (registration.payment_status === 'paid' && course != null) {
    return <Navigate to={`/courses/${course.course_token}/register`}></Navigate>
  }

  return (
    <RegistrationLayout>
      <Card
        className='registration-layout'
        style={{
          backgroundColor: '#23426B',
          color: 'white'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 50, marginTop: 20 }}>
          <h2>Payment</h2>
        </div>
        <Row style={{ marginBottom: 15 }}>
          <Col md={18}>{course.name}</Col>
          <Col md={6}>
            {currency} {registration.registration_fee}
          </Col>
        </Row>
        {registration.coupon_code != null && (
          <Row style={{ marginBottom: 15 }}>
            <Col md={18}>Discount ({registration.coupon_code})</Col>
            <Col md={6}>
              - {currency} {registration.discount_amount}
            </Col>
          </Row>
        )}
        <Row style={{ marginBottom: 15 }}>
          <Col md={18}>Payment Fee</Col>
          <Col md={6}>
            {currency} {registration.payment_fee}
          </Col>
        </Row>
        <Row style={{ marginBottom: 15 }}>
          <Col md={18}></Col>
          <Col md={6}>No tax</Col>
        </Row>
        <Row>
          <hr style={{ width: '100%' }}></hr>
          <Col md={18}>
            <h3>Total </h3>
          </Col>
          <Col md={6}>
            <h3>
              {currency} {registration.payment_amount}
            </h3>
          </Col>
        </Row>
        <Row style={{ marginBottom: 5 }}>
          <Col>
            <a
              onClick={() => {
                setOpenModal(true)
              }}
            >
              Do you have a promo code?
            </a>
          </Col>
        </Row>
      </Card>

      <Modal
        title='Enter your coupon code'
        okText='Submit'
        open={openModal}
        confirmLoading={addCouponCodeMutation.isPending}
        onCancel={() => {
          setOpenModal(false)
        }}
        onOk={() => {
          submitCoupon()
        }}
        destroyOnHidden
      >
        <div>
          <Form form={form} preserve={false}>
            <Form.Item name='coupon' rules={[{ required: true }]}>
              <Input placeholder='Enter your coupon code...' />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <Card
        style={{
          maxWidth: '100%',
          margin: 'auto',
          marginBottom: 40,
          borderRadius: 0
        }}
      >
        <Checkbox
          onChange={(e) => {
            setChecked(e.target.checked)
          }}
          style={{ marginBottom: 20 }}
        >
          I agree to the{' '}
          <a href='/terms-and-conditions' target='_blank'>
            Terms and Conditions
          </a>
        </Checkbox>
        <div style={{ marginBottom: 10 }}>
          <Button
            type='primary'
            size='large'
            className={
              checked ? 'register-form-button' : 'register-form-button disable'
            }
            onClick={() => {
              checkoutMutation.mutate(registration.id)
            }}
            disabled={!checked}
            loading={checkoutMutation.isPending}
          >
            Pay {currency} {registration.payment_amount}
          </Button>
        </div>
        <div>
          <Link to={`/courses/${course.course_token}/register`}>Cancel</Link>
        </div>
      </Card>
    </RegistrationLayout>
  )
}
